/* eslint-disable global-require */
import React, { PropsWithChildren, useContext, useEffect, useRef } from 'react'
import { useIntl } from 'react-intl'

import { Button, ButtonModel, IconRegularClose, Spinner } from '@notino/react-styleguide'

// utils
import { AppContext } from '../../utils/appProvider'
import { getLinkAttributes } from '../../utils/helper'

// hooks
import useMessages from '../../hooks/useMessages'

// styles
import * as SC from './LoginScreenStyles'

// types
import { CmsLoginAndRegistrationBannerData } from '../../types/types'

// components
import VisuallyHidden from '../VisuallyHidden/VisuallyHidden'

type Props = PropsWithChildren<{
	onLogin: (e: React.SyntheticEvent<HTMLElement>) => void
	onRegister: (e: React.SyntheticEvent<HTMLElement>) => void
	onContinueAsGuest: (e: React.SyntheticEvent<HTMLElement>) => void | Promise<void>
	onClose?: () => void
	cmsData: CmsLoginAndRegistrationBannerData
	isContinueAsGuestLoading?: boolean
	showChildren?: boolean
}>

const LoginScreen = (props: Props) => {
	const { messages } = useMessages()
	const { locale, formatMessage } = useIntl()
	const { isAuthorized, productionEnvironment } = useContext(AppContext)
	const wrapperRef = useRef<HTMLDivElement>(null)
	const titleRef = useRef<HTMLHeadingElement>(null)

	const { onLogin, onRegister, onContinueAsGuest, onClose, cmsData, children, isContinueAsGuestLoading = false, showChildren = true } = props
	const { image: promoBanner } = cmsData

	const isShowingLogin = !isAuthorized && !showChildren
	const dialogAriaLabel = formatMessage({ id: 'Sign in or register', defaultMessage: 'Sign in or register' })
	const continueAsGuestLabel = messages['Continue as guest']

	useEffect(() => {
		if (!isShowingLogin) {
			return undefined
		}

		const dialog = wrapperRef.current?.closest<HTMLElement>('[role="dialog"]')

		if (!dialog) {
			return undefined
		}

		const previousAriaLabel = dialog.getAttribute('aria-label')
		dialog.setAttribute('aria-label', dialogAriaLabel)

		return () => {
			if (previousAriaLabel) {
				dialog.setAttribute('aria-label', previousAriaLabel)
			} else {
				dialog.removeAttribute('aria-label')
			}
		}
	}, [isShowingLogin, dialogAriaLabel])

	useEffect(() => {
		if (!isShowingLogin) {
			return
		}

		titleRef.current?.focus()
	}, [isShowingLogin])

	const promoBannerImage = <img src={promoBanner.url} alt={promoBanner.alt} loading='lazy' width={295} height={295} />

	const promoBannerPicture = promoBanner.urlWebP ? (
		<SC.Picture>
			{promoBanner.url && <source srcSet={promoBanner.urlWebP} type='image/webp' />}
			{promoBannerImage}
		</SC.Picture>
	) : (
		promoBannerImage
	)

	if (!isShowingLogin) {
		return children
	}

	return (
		<SC.LoginScreenWrapper ref={wrapperRef}>
			{onClose && (
				<SC.CloseButton type={'button'} onClick={() => onClose()} aria-label={messages['Close modal']}>
					<IconRegularClose color={'icon.primary'} width={'1.5rem'} height={'1.5rem'} aria-hidden />
				</SC.CloseButton>
			)}
			<SC.MainContent>
				<SC.ImageWrapper>
					{promoBanner.link ? (
						<SC.PromoBannerLink href={promoBanner.link} {...getLinkAttributes(promoBanner.link, productionEnvironment, locale)}>
							{promoBannerPicture}
						</SC.PromoBannerLink>
					) : (
						promoBannerPicture
					)}
				</SC.ImageWrapper>
				<SC.Title ref={titleRef} tabIndex={-1}>
					{messages['With the account, you will be in control of all bookings']}
				</SC.Title>
				<SC.Description>{messages['Simple and in one place. Including bookings in a few clicks.']}</SC.Description>
			</SC.MainContent>
			<SC.Footer>
				<Button buttonStyle={ButtonModel.Styles.primary} onClick={onLogin}>
					{messages['Sign in']}
				</Button>
				<Button buttonStyle={ButtonModel.Styles.secondary} onClick={onRegister}>
					{messages.Register}
				</Button>
				<SC.LinkButton
					buttonStyle={ButtonModel.Styles.primary}
					onClick={onContinueAsGuest}
					disabled={isContinueAsGuestLoading}
					aria-busy={isContinueAsGuestLoading}
				>
					{isContinueAsGuestLoading ? (
						<>
							<Spinner size={16} color={'icon.disabled'} aria-hidden />
							<VisuallyHidden>{continueAsGuestLabel}</VisuallyHidden>
						</>
					) : (
						continueAsGuestLabel
					)}
				</SC.LinkButton>
			</SC.Footer>
		</SC.LoginScreenWrapper>
	)
}

export default LoginScreen
